import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Badge } from "@radix-ui/themes";
import { Search, Utensils, Star, ChevronRight, X } from 'lucide-react';
import TurboLogoIcon from '../components/TurboLogoIcon';
import { useLanguage } from '../contexts/LanguageContext';
import { menuBookAPI } from '../services/api';

interface Restaurant {
  _id: string;
  slug: string;
  name: string;
  image: string;
  category: string;
  rating: { average: number; count: number };
  address: { street: string; city: string; state: string };
  operatingHours: Record<string, { open: string; close: string; isOpen: boolean }>;
  isCurrentlyOpen: boolean | null;
  isFeatured: boolean;
}

// Category translation key mapping
const categoryTranslationKeys: Record<string, string> = {
  'all': 'allCategories',
  'Fast food': 'fastFood',
  'Pizza': 'pizza',
  'Asian': 'asian',
  'Middle-eastern': 'middleEastern',
  'Desserts': 'desserts',
  'Healthy': 'healthy',
  'Coffee': 'coffee',
};

const categories = ['all', 'Fast food', 'Pizza', 'Asian', 'Middle-eastern', 'Desserts', 'Healthy', 'Coffee'];

const MenuBookPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, language, isRTL, setLanguage } = useLanguage();

  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Debounce search
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 400);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchQuery]);

  // Fetch restaurants
  useEffect(() => {
    const fetchRestaurants = async () => {
      if (page === 1) setLoading(true);
      else setLoadingMore(true);
      try {
        const params: any = { page, limit: 20 };
        if (debouncedSearch) params.search = debouncedSearch;
        if (selectedCategory !== 'all') params.category = selectedCategory;
        const { data } = await menuBookAPI.getRestaurants(params);
        if (page === 1) {
          setRestaurants(data.restaurants || []);
        } else {
          setRestaurants(prev => [...prev, ...(data.restaurants || [])]);
        }
        setTotalPages(data.totalPages || 1);
      } catch (e) {
        console.error('Error fetching restaurants:', e);
      }
      setLoading(false);
      setLoadingMore(false);
    };
    fetchRestaurants();
  }, [debouncedSearch, selectedCategory, page]);

  const handleCategoryChange = (cat: string) => {
    setSelectedCategory(cat);
    setPage(1);
  };

  const getCategoryLabel = (category: string) => {
    const key = categoryTranslationKeys[category];
    return key ? t(key) : category;
  };

  // Loading state
  if (loading && page === 1) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <Helmet>
          <title>{t("menuBook")} | Turbo</title>
          <meta name="description" content={t("browseRestaurants")} />
        </Helmet>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">{t("loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-50 dark:bg-gray-900 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <Helmet>
        <title>{t("menuBook")} | Turbo</title>
        <meta name="description" content={t("browseRestaurants")} />
        <meta property="og:title" content={`${t("menuBook")} | Turbo`} />
        <meta property="og:description" content={t("browseRestaurants")} />
      </Helmet>

      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-30 bg-white dark:bg-gray-800 shadow-sm">
        <div className="px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <TurboLogoIcon size={22} className="text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-gray-900 dark:text-white truncate">
                {t("menuBook")}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("browseRestaurants")}
              </p>
            </div>
            <button
              onClick={() => setLanguage(language === 'en' ? 'ar' : 'en')}
              className="px-3 py-1.5 text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {language === 'en' ? 'عربي' : 'English'}
            </button>
          </div>
        </div>
      </header>

      {/* Search Bar - Fixed below header */}
      <div className="fixed top-[60px] left-0 right-0 z-20 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="px-4 py-3">
          <div className="relative">
            <Search className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 ${isRTL ? 'right-3' : 'left-3'}`} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("searchRestaurants")}
              className={`w-full ${isRTL ? 'pr-10 pl-10' : 'pl-10 pr-10'} py-2.5 bg-gray-100 dark:bg-gray-700 border-0 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500`}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-3' : 'right-3'}`}
              >
                <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Category Tabs - Fixed below search */}
      <div className="fixed top-[120px] left-0 right-0 z-20 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="overflow-x-auto scrollbar-hide">
          <div className="flex gap-3 px-4 py-4">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => handleCategoryChange(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${selectedCategory === category
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
              >
                {getCategoryLabel(category)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="pt-[180px] pb-24 px-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {restaurants.map(restaurant => (
            <div
              key={restaurant._id}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm overflow-hidden border border-gray-100 dark:border-gray-700"
            >
              {/* Restaurant Image */}
              <div
                className="relative h-40 bg-gray-100 dark:bg-gray-700 cursor-pointer"
                onClick={() => navigate(`/menu/${restaurant.slug}`)}
              >
                {restaurant.image ? (
                  <img
                    src={restaurant.image}
                    alt={restaurant.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Utensils className="w-12 h-12 text-gray-300 dark:text-gray-600" />
                  </div>
                )}
                {/* Category badge */}
                <div className={`absolute top-2 ${isRTL ? 'right-2' : 'left-2'}`}>
                  <span className="px-2 py-1 text-xs font-medium bg-white/90 dark:bg-gray-800/90 text-gray-700 dark:text-gray-300 rounded-full">
                    {getCategoryLabel(restaurant.category)}
                  </span>
                </div>
                {/* Open/Closed badge */}
                {restaurant.isCurrentlyOpen !== null && (
                  <div className={`absolute top-2 ${isRTL ? 'left-2' : 'right-2'}`}>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full flex items-center gap-1 ${
                      restaurant.isCurrentlyOpen
                        ? 'bg-green-400 text-green-900'
                        : 'bg-red-400 text-red-900'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${restaurant.isCurrentlyOpen ? 'bg-green-700' : 'bg-red-700'}`}></span>
                      {t(restaurant.isCurrentlyOpen ? 'currentlyOpen' : 'currentlyClosed')}
                    </span>
                  </div>
                )}
                {/* Featured badge */}
                {restaurant.isFeatured && (
                  <div className="absolute bottom-2 left-2 right-2 flex justify-center">
                    <span className="px-3 py-1 text-xs font-medium bg-yellow-400 text-yellow-900 rounded-full flex items-center gap-1">
                      <Star className="w-3 h-3 fill-current" />
                      {t("featuredItems")}
                    </span>
                  </div>
                )}
                {/* Tap indicator */}
                {!restaurant.isFeatured && (
                  <div className="absolute bottom-2 left-2 right-2 flex justify-center">
                    <span className="px-3 py-1 text-xs font-medium bg-black/50 text-white rounded-full flex items-center gap-1">
                      {t("viewRestaurantMenu")}
                      <ChevronRight className="w-3 h-3" />
                    </span>
                  </div>
                )}
              </div>

              {/* Restaurant Info */}
              <div className="p-4">
                <div className="flex justify-between items-start gap-2 mb-1">
                  <h3
                    className="font-semibold text-gray-900 dark:text-white line-clamp-1 cursor-pointer hover:text-red-600"
                    onClick={() => navigate(`/menu/${restaurant.slug}`)}
                  >
                    {restaurant.name}
                  </h3>
                </div>
                {/* Rating */}
                {restaurant.rating && restaurant.rating.count > 0 && (
                  <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 mb-2">
                    <Star className="w-4 h-4 text-yellow-400 fill-current" />
                    <span>{restaurant.rating.average.toFixed(1)}</span>
                    <span>({restaurant.rating.count})</span>
                  </div>
                )}
                {/* Address */}
                {restaurant.address && (
                  <p className="text-gray-500 dark:text-gray-400 text-sm mb-4 line-clamp-1">
                    {restaurant.address.city}{restaurant.address.street ? ` • ${restaurant.address.street}` : ''}
                  </p>
                )}
                {/* View Menu Button */}
                <button
                  onClick={() => navigate(`/menu/${restaurant.slug}`)}
                  className="w-full bg-red-600 hover:bg-red-700 text-white rounded-xl py-2.5 font-medium flex items-center justify-center gap-2 transition-colors"
                >
                  {t("viewRestaurantMenu")}
                  <ChevronRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {restaurants.length === 0 && (
          <div className="text-center py-12">
            <Utensils className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400 font-medium">{t("noRestaurantsFound")}</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">{t("tryDifferentSearch")}</p>
          </div>
        )}

        {/* Load More */}
        {page < totalPages && restaurants.length > 0 && (
          <div className="flex justify-center mt-6">
            <button
              onClick={() => setPage(prev => prev + 1)}
              disabled={loadingMore}
              className="px-6 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              {loadingMore ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                  {t("loading")}
                </div>
              ) : (
                t("next")
              )}
            </button>
          </div>
        )}
      </main>

      {/* Footer */}
      <div className="text-center py-6 text-xs text-gray-400 dark:text-gray-500">
        {t("poweredByTurbo")}
      </div>

      {/* Custom Styles */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default MenuBookPage;
